import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import Template from '../../components/template'
import { user, workflows } from '../../lib/api'
import { SelectEnteries, User, WorkflowConfig, WorkflowAssignedUsers } from '../../lib/types'
import * as jsyaml from 'js-yaml'
import Form from '../../components/form'
import MCCache from '../../lib/cache'
import { getWorkflow, workflowStatusOptions } from '../util'
import { DefaultConfig, StepConfigs } from './config-templates'
import StepTypeLabels from '../../steps/labels'
import ErrorMsg from '../../components/error-msg'
import CodeEditor from '@uiw/react-textarea-code-editor'
import './edit.css'
import SelectWrapper from '../../components/selectWrapper/SelectWrapper'
import auth from '../../lib/auth'

export default function WorkflowEditPage() {
    const { id } = useParams()
    const isNew = id === '_'
    const [status, setStatus] = useState(workflowStatusOptions[1])
    const [config, setConfig] = useState('')
    const [stepType, setStepType] = useState('')
    const [dirty, setDirty] = useState(false)
    const [valid, setValid] = useState(true)
    const [previewConfig, setPreviewConfig] = useState<WorkflowConfig | null>(null)
    const [step, setStep] = useState(0)
    const [err, setErr] = useState('')
    const [users, setUsers] = useState<User[]>([])
    const [contributors, setContributors] = useState<SelectEnteries[] | []>([])
    const [reviewers, setReviewers] = useState<SelectEnteries[] | []>([])
    const [loadingUsers, setLoadingUsers] = useState(true)
    const formattedOptions = users.map((user) => ({
        value: user.id,
        label: user.name,
    }))
    const isAdmin = useMemo(() => auth.checkRole('admin', 'workflowAdmin'), [])
    const navigate = useNavigate()

    useEffect(() => {
        user.list('', '').then((users: User[]) => {
            setUsers(users)
            setLoadingUsers(false)
        })
        if (isNew) {
            setConfig(DefaultConfig)
            setValid(true)
            setPreviewConfig(jsyaml.load(DefaultConfig) as WorkflowConfig)
            return
        }
        getWorkflow(Number(id))
            .then((wf) => {

                setConfig(wf.config)
                setValid(!!wf.valid)
                if (wf.users) {
                    let contributor: SelectEnteries[] = []
                    let reviewer: SelectEnteries[] = []
                    wf.users.forEach((user: WorkflowAssignedUsers) => {
                        if (user.role === 'contributor') {
                            contributor.push({ label: user.name, value: user.id })
                        }
                        if (user.role === 'reviewer') {
                            reviewer.push({ label: user.name, value: user.id })
                        }
                    })
                    setContributors(contributor)
                    setReviewers(reviewer)
                }
                const status = workflowStatusOptions.find(item => item.id === wf.statusId)
                status && setStatus(status)
                if (wf.valid) setPreviewConfig(jsyaml.load(wf.config) as WorkflowConfig)
            })
            .catch((err) => setErr(err.message || err))
    }, [id, isNew])

    function updateConfig(config: string) {
        setConfig(config)
        setDirty(true)
    }

    const updateStatus = (status: any) => {
        setStatus(status)
        setDirty(true)
    }

    const updateContributor = (value: SelectEnteries[]) => {
        setContributors(value)
        setDirty(true)
    }

    const updateReviwer = (value: SelectEnteries[]) => {
        setReviewers(value)
        setDirty(true)
    }

    const updatePreview = useCallback(() => {
        workflows
            .validate(config)
            .then(({ valid }) => {
                setValid(valid)
                if (valid) setPreviewConfig(jsyaml.load(config) as WorkflowConfig)
            })
            .catch((err) => setErr(err.message || err))
    }, [config])

    function save() {
        setDirty(false)
        if (isNew) {
            workflows
                .create(config, contributors, reviewers, status)
                .then(({ id }) => navigate('/workflows/' + id))
                .catch((err) => setErr(err.message || err))
        } else {
            MCCache.delete('workflow:' + id)
            MCCache.delete('workflow-config:' + id)
            workflows
                .save(Number(id), config, status, contributors, reviewers)
                .then(() => navigate('/workflows/' + id))
                .catch((err) => setErr(err.message || err))
        }
    }

    function addStep() {
        setConfig(config + StepConfigs[stepType])
        setDirty(true)
        setStepType('')
    }

    const lines = config.split('\n').map(() => false)
    const isUserAssigned = useMemo(
        () => contributors.length > 0 && reviewers.length > 0,
        [contributors, reviewers],
    )
    const statusOptions = useMemo(() => workflowStatusOptions.filter((item) => item.id >= 1), [])
    const stepsOptions = useMemo(
        () =>
            Object.keys(StepConfigs).map((type) => ({ label: StepTypeLabels[type], value: type })),
        [],
    )

    return (
        <Template auth={['admin', 'workflowAdmin', 'lead']}>
            <div className='container mx-auto mt-10'>
                {!isNew && (
                    <Link to={'/workflows/' + id}>
                        <i className='lni lni-chevron-left' /> Back to Workflow
                    </Link>
                )}
                <h1 className='mt-3'>{isNew ? 'New' : 'Edit'} Workflow</h1>

                {err && <ErrorMsg error={err} />}
                <div className='my-5'>
                    <div className=''>
                        <div>
                            <span className='text-gray-400 mr-2'>Contributors:</span>
                            <SelectWrapper
                                isMulti
                                options={formattedOptions}
                                placeholder='Select contributors'
                                closeMenuOnSelect={false}
                                isLoading={loadingUsers}
                                value={contributors}
                                onChange={(selected) => updateContributor(selected)}
                                className='w-full'
                                maxOptionsToShow={10}
                                />
                        </div>

                        <div>
                            <span className='text-gray-400 mr-2'>Reviewers:</span>
                            <SelectWrapper
                                isMulti
                                options={formattedOptions}
                                placeholder='Select reviewers'
                                closeMenuOnSelect={false}
                                isLoading={loadingUsers}
                                value={reviewers}
                                onChange={(selected) => updateReviwer(selected)}
                                className='w-full'
                                maxOptionsToShow={10}
                            />
                        </div>
                        {isAdmin && <div className='flex'>
                            <div className='flex flex-col flex-1 py-1 border-l-2'>
                                <span className='text-gray-400 mr-2'>Update Status:</span>
                                <SelectWrapper
                                    options={statusOptions}
                                    placeholder='Select status'
                                    value={status}
                                    onChange={(selected) => updateStatus(selected)}
                                    hideSelectedOptions
                                    className='w-full'
                                />
                            </div>
                            <div className='flex flex-col flex-1 ml-4 py-1 border-l-2'>
                                <span className='text-gray-400 mr-2'>Add Step:</span>
                                <div className='flex'>
                                    <SelectWrapper
                                        options={stepsOptions}
                                        placeholder='Select step'
                                        value={stepType}
                                        onChange={(selected) => setStepType(selected.value)}
                                        hideSelectedOptions
                                        className='w-full'
                                    />
                                    <button
                                        className='ml-2 blue'
                                        disabled={!stepType}
                                        onClick={addStep}
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>}
                    </div>

                    <div className='flex justify-between items-center my-4'>
                        {isAdmin ? <button className='blue' onClick={updatePreview}>
                            <i className='lni lni-eye' /> Update Preview
                        </button> : <div></div>}
                        <div>
                            <a
                                className='button ml-2'
                                href='https://gitlab.com/torch-insight/master-of-ceremonies/ringmaster-mc/-/blob/main/config-guide.md'
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <i className='lni lni-help' /> Help
                            </a>
                            <button className='blue ml-2' disabled={!dirty || !isUserAssigned} onClick={save}>
                                <i className='lni lni-save' /> Save
                            </button>
                        </div>
                    </div>
                </div>
                {isAdmin && <div className='flex gap-4'>
                    <div className='w-1/2'>
                        <div id='editor-holder'>
                            <div id='editor'>
                                <CodeEditor
                                    value={config}
                                    language='yaml'
                                    placeholder='Please enter JS code.'
                                    onChange={(e) => updateConfig(e.target.value)}
                                    rows={lines.length}
                                />
                                {/* <textarea value={config} onChange={e => updateConfig(e.target.value)} rows={lines.length} /> */}
                                <div id='line-numbers'>
                                    {lines.map((_, i) => (
                                        <div key={i}>{i + 1}</div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='w-1/2'>
                        {valid && previewConfig && previewConfig.steps ? (
                            <div className='pl-2'>
                                Step:
                                <select
                                    value={step}
                                    onChange={(e) => setStep(Number(e.target.value))}
                                    className='ml-2'
                                >
                                    {previewConfig.steps.map((step, i) => (
                                        <option value={i} key={i}>
                                            {step.name}
                                        </option>
                                    ))}
                                </select>
                                <hr className='my-2' />
                                {(previewConfig.steps[step].type === 'input' || previewConfig.steps[step].type === 'input-cycle') && (
                                    <Form config={previewConfig.steps[step].view!} />
                                )}
                                {(previewConfig.steps[step].type === 'correct' ||
                                    previewConfig.steps[step].type === 'correct-cycle') && (
                                        <Form config={previewConfig.steps[step].view!} />
                                    )}
                                {previewConfig.steps[step].type === 'review' && (
                                    <div>Standard Review Form</div>
                                )}
                                {previewConfig.steps[step].type === 'approve' && (
                                    <div>Standard Approval Form</div>
                                )}
                                {previewConfig.steps[step].type === 'arbitrate' && (
                                    <div>Standard Arbitrate Form</div>
                                )}
                            </div>
                        ) : (
                            <div className='text-red-500'>Invalid Workflow Config</div>
                        )}
                    </div>
                </div>}
            </div>
        </Template>
    )
}
